export const
    DEFAULT_RATE_LIMIT_MS = 60 * 1000,
    RATE_LIMIT_STORAGE_KEY = 'upi.sentry.rate-limit.v1';

const ERROR_CATEGORIES = ['default', 'error', 'event', 'security'];

function getDelayFromHeader ( value, now ) {
    const seconds = parseInt( value, 10 );
    if ( !isNaN( seconds ) ) {
        return Math.max( 0, seconds * 1000 );
    }

    const date = Date.parse( value );
    return isNaN( date ) ? 0 : Math.max( 0, date - now );
}

function appliesToErrors ( categoryValue = '' ) {
    const categories = categoryValue.split( ';' ).filter( Boolean );
    return !categories.length || categories.some( category => ERROR_CATEGORIES.indexOf( category ) >= 0 );
}

export function getRateLimitUntil ( headers = {}, status, now = Date.now() ) {
    const sentryLimits = headers['x-sentry-rate-limits'];
    let until = 0;

    if ( sentryLimits ) {
        sentryLimits.split( ',' ).forEach( limit => {
            const parameters = limit.trim().split( ':' );
            if ( appliesToErrors( parameters[1] ) ) {
                const delay = getDelayFromHeader( parameters[0], now ) || DEFAULT_RATE_LIMIT_MS;
                until = Math.max( until, now + delay );
            }
        } );
    }

    if ( until || status !== 429 ) {
        return until;
    }

    const retryAfter = headers['retry-after'];
    return now + (retryAfter ? getDelayFromHeader( retryAfter, now ) || DEFAULT_RATE_LIMIT_MS : DEFAULT_RATE_LIMIT_MS);
}

function getSessionStorage () {
    try {
        return typeof window !== 'undefined' ? window.sessionStorage : null;
    }
    catch ( error ) {
        return null;
    }
}

export default class RateLimitState {
    constructor ( options = {} ) {
        this.now = options.now || Date.now;
        this.storage = options.storage === undefined ? getSessionStorage() : options.storage;
        this.storageKey = options.storageKey || RATE_LIMIT_STORAGE_KEY;
        this.active = this.read();
    }

    suppressUntil ( until, reason = 'rate-limited' ) {
        if ( typeof until !== 'number' || until <= this.now() ) {
            return;
        }

        if ( !this.active || until > this.active.until ) {
            this.active = {reason, until};
            this.write();
        }
    }

    getActive () {
        if ( !this.active || this.active.until <= this.now() ) {
            this.clear();
            return null;
        }
        return this.active;
    }

    read () {
        if ( !this.storage ) {
            return null;
        }

        try {
            const parsed = JSON.parse( this.storage.getItem( this.storageKey ) );
            return parsed && typeof parsed.until === 'number' ? parsed : null;
        }
        catch ( error ) {
            this.removeStoredValue();
            return null;
        }
    }

    write () {
        if ( !this.storage ) {
            return;
        }
        try {
            this.storage.setItem( this.storageKey, JSON.stringify( this.active ) );
        }
        catch ( error ) {
            // Storage can be blocked; the in-memory cooldown remains active.
        }
    }

    clear () {
        this.active = null;
        this.removeStoredValue();
    }

    removeStoredValue () {
        if ( !this.storage ) {
            return;
        }
        try {
            this.storage.removeItem( this.storageKey );
        }
        catch ( error ) {
            // Storage can be blocked without affecting application behavior.
        }
    }
}

export const rateLimitState = new RateLimitState();
